/** External */
import { gql } from "lib/apolloClient";

/** Relative **/
import { courseDetailsFragment } from "./ToddleAnalyticsFragments";

export const getOverviewMetricsQuery = gql`
  query getOverviewMetrics(
    $id: ID!
    $overViewMetricFilters: OverviewMetricsFilterInput!
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        analytics {
          overviewMetrics(filter: $overViewMetricFilters) {
            field
            stringField
            value
            metaData
          }
        }
      }
    }
  }
`;

export const getTableMetricsQuery = gql`
  query getTableMetrics(
    $id: ID!
    $tableMetricFilters: IndividualMetricsFilterInput!
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        analytics {
          individualMetrics(filter: $tableMetricFilters) {
            totalCount
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              field
              stringField
              value
              metaData
            }
          }
        }
      }
    }
  }
`;

export const getGraphDataQuery = gql`
  query getGraphData($id: ID!, $graphDataFilters: GraphFilterInput!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        analytics {
          graphData(filter: $graphDataFilters) {
            xAxis
            yAxis {
              key
              value
              metaData
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationGradesQuery = gql`
  query getOrganizationGrades($id: ID!, $gradeFilters: GradeFilterInput) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        grades(filters: $gradeFilters) {
          id
          name
          curriculumProgram {
            id
          }
        }
      }
    }
  }
`;

export const getOrganizationSubjectsQuery = gql`
  query getOrganizationSubjects(
    $id: ID!
    $subjectFilters: SubjectFilterInput
    $subjectCourseFilters: SubjectCourseFilters
    $flatVariantsCourseFilters: FlatVariantsCourseFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        subjects(filters: $subjectFilters) {
          id
          name
          grades {
            id
            name
            curriculumProgram {
              id
              type
            }
          }
          flatVariants {
            level {
              id
              label
              type
            }
            courses(filters: $flatVariantsCourseFilters) {
              edges {
                node {
                  ...courseDetails
                }
              }
            }
          }
          courses(filters: $subjectCourseFilters) {
            edges {
              node {
                ...courseDetails
              }
            }
          }
        }
      }
    }
  }
  ${courseDetailsFragment}
`;

export const getOrganizationPointSetQuery = gql`
  query getOrganizationPointSet($id: ID!, $filters: PointAssessmentSetFilter) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        pointAssessmentSets(filters: $filters) {
          edges {
            node {
              id
              associatedEntities {
                ... on AcademicTerm {
                  id
                  label
                }
              }
              categories {
                id
                label
                values {
                  id
                  value
                  genericTag {
                    id
                    label
                  }
                }
                gradeBoundary {
                  id
                  categories {
                    id
                    label
                    displaySequence
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getStaffCurriculumQuery = gql`
  query getStaffCurriculum(
    $ids: [ID!]
    $userCurriculumProgramFilter: UserCurriculumProgramFilter
  ) {
    platform {
      staff(ids: $ids) {
        curriculumPrograms(filters: $userCurriculumProgramFilter) {
          id
          type
          nodeInterfaceType
          label
          acronym
          ... on UBDCurriculumProgram {
            id
            variant
          }
        }
      }
    }
  }
`;

export const getAnalyticsEntityQuery = gql`
  query getAnalyticsEntity(
    $id: ID!
    $analyticsEntityFilterInput: AnalyticsEntityFilterInput!
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        analytics {
          analyticsEntityData(filter: $analyticsEntityFilterInput) {
            totalCount
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              entityId
              entityType
              name
              curriculumProgramIds
              academicYearId
              createdAt
              metaData
            }
          }
        }
      }
    }
  }
`;

export const getGradingPeriodsQuery = gql`
  query getGradingPeriods($id: ID!) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        id
        academicYears {
          id
          isCurrentAcademicYear
          isUpcomingAcademicYear
          startDate
          endDate
          terms {
            id
            label
            abbreviation
            type
            startDate
            endDate
            curriculumProgram {
              id
              type
            }
          }
        }
      }
    }
  }
`;

export const getBehaviourModuleNomenclatureQuery = gql`
  query getNomenclature($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        globalConstants(
          filter: {
            names: [
              "BEHAVIOUR_MODULE_NOMENCLATURE_MODULE"
              "BEHAVIOUR_MODULE_NOMENCLATURE_INCIDENT"
              "BEHAVIOUR_MODULE_NOMENCLATURE_ACTION"
              "BEHAVIOUR_MODULE_NOMENCLATURE_LEVEL"
            ]
          }
        ) {
          id
          name
          value
        }
      }
    }
  }
`;

export const getAnalyticsDataExportQuery = gql`
  query getAnalyticsDataExport(
    $id: ID!
    $exportFilters: MetricDataExportFilterInput!
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        analytics {
          metricDataExport(filter: $exportFilters) {
            url
          }
        }
      }
    }
  }
`;

export const getLastSyncTimeQuery = gql`
  query getLastSyncTime(
    $id: ID!
    $lastSyncTimeFilterInput: LastSyncTimeFilterInput!
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        analytics {
          lastSyncTime(filter: $lastSyncTimeFilterInput) {
            entityId
            entityType
            timestamp
          }
        }
      }
    }
  }
`;

export const checkManualSyncEligibility = gql`
  query CheckManualSync($id: ID!, $input: ManualSyncInput!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        analytics {
          checkManualSync(filter: $input) {
            isEligible
            lastRefreshedAt
          }
        }
      }
    }
  }
`;

export const getOrganizationLearningCoursesQuery = gql`
  query getOrganizationLearningCourses(
    $organizationId: ID!
    $learningCourseFilters: LearningCourseFilters
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        learningCourses(filters: $learningCourseFilters) {
          count {
            totalCount
          }
          edges {
            node {
              id
              title
              type
              grades {
                id
                name
              }
              gradingPeriods {
                edges {
                  node {
                    id
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
